import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { mediaUrl } from "../../lib/api";
import { buildBookingUrl, goBooking, formatDateRange, scheduleStatus } from "../../lib/promo";
import { useLang, pick } from "../../lib/i18n";
import CaptionedImage from "../CaptionedImage";

export default function Offers({ offers, bookingUrl }) {
  const { t, lang } = useLang();
  if (!offers?.length) return null;

  const onBook = (offer) => {
    // Opens the embedded /book page with the promo code carried into the Exely engine
    const target = buildBookingUrl(offer.booking_url || bookingUrl, offer.promo_code || "");
    if (!target) {
      toast.info(t("book.notConfigured"));
      return;
    }
    goBooking(target);
  };

  return (
    <section id="offers" data-testid="offers-section" className="py-24 sm:py-32 bg-[#F4EFEA]">
      <div className="max-w-7xl mx-auto px-5 sm:px-8">
        <motion.p
          initial={{ opacity: 0, y: 16 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 0.7 }}
          className="text-xs uppercase tracking-[0.25em] font-sans font-medium text-[#9A7B66] mb-5"
        >
          {t("offers.eyebrow")}
        </motion.p>
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-6 mb-14">
          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, delay: 0.1 }}
            className="font-serif font-normal tracking-tight text-[#2C2825] leading-tight text-3xl sm:text-4xl lg:text-5xl"
          >
            {t("offers.title")}
          </motion.h2>
          <motion.p
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="max-w-sm text-sm text-[#A0988E] font-sans leading-relaxed"
          >
            {t("offers.intro")}
          </motion.p>
        </div>

        <div className="grid md:grid-cols-2 gap-8 lg:gap-12">
          {offers.map((offer, i) => {
            const status = scheduleStatus(offer.start_date, offer.end_date);
            return (
            <motion.article
              key={offer.id || i}
              data-testid={`offer-card-${i}`}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-60px" }}
              transition={{ duration: 0.8, delay: i * 0.12 }}
              className="group bg-white border border-[#E5DED4] rounded-md overflow-hidden flex flex-col"
            >
              <div className="relative overflow-hidden h-64">
                <CaptionedImage
                  testId={`offer-image-${i}`}
                  src={mediaUrl(offer.image_url)}
                  alt={offer.image_alt || pick(offer, "title", lang)}
                  title={offer.image_title || pick(offer, "title", lang)}
                  description={offer.image_description || pick(offer, "description", lang)}
                  caption={offer.image_caption || ""}
                  focalPoint={offer.image_focal_point || "50% 0%"}
                  className="w-full h-full"
                  imageClassName="w-full h-full object-cover transition-transform duration-[1.3s] ease-out group-hover:scale-105"
                />
                {offer.tag && (
                  <span className="absolute top-4 left-4 bg-[#9A7B66] text-[#FBF9F5] text-[11px] font-sans font-semibold uppercase tracking-[0.16em] px-4 py-1.5 rounded-sm">
                    {offer.tag}
                  </span>
                )}
              </div>
              <div className="p-7 flex flex-col flex-1">
                <div className="flex items-start justify-between gap-4">
                  <h3 className="font-serif font-normal text-2xl text-[#2C2825] leading-snug">{pick(offer, "title", lang)}</h3>
                  {(offer.start_date || offer.end_date) && (
                    <span
                      data-testid={`offer-status-${i}`}
                      className={`shrink-0 rounded-full px-3 py-1 text-[10px] font-sans font-semibold uppercase tracking-[0.14em] ${
                        status === "active" ? "bg-[#E8F0EC] text-[#2D5A3F]" : status === "upcoming" ? "bg-[#F8F3E8] text-[#8A622A]" : "bg-[#F7ECEC] text-[#7A3E3E]"
                      }`}
                    >
                      {status}
                    </span>
                  )}
                </div>
                {(offer.start_date || offer.end_date) && (
                  <p data-testid={`offer-validity-${i}`} className="mt-2 text-[11px] uppercase tracking-[0.14em] text-[#A0988E] font-sans">
                    {formatDateRange(offer.start_date, offer.end_date)}
                  </p>
                )}
                <p className="mt-3 text-sm text-[#6E675F] font-sans leading-relaxed flex-1">{pick(offer, "description", lang)}</p>
                <div className="mt-6 flex flex-wrap items-center gap-4">
                  <button
                    data-testid={`offer-book-button-${i}`}
                    onClick={() => onBook(offer)}
                    className="group/btn inline-flex items-center gap-2 px-7 py-3 rounded-sm border border-[#9A7B66] text-[#9A7B66] text-[12px] font-sans font-semibold uppercase tracking-[0.14em] hover:bg-[#9A7B66] hover:text-[#FBF9F5] transition-all duration-300"
                  >
                    {t("offers.book")}
                    <ArrowUpRight className="w-4 h-4 transition-transform duration-300 group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
                  </button>
                  <Link
                    data-testid={`offer-detail-link-${i}`}
                    to={`/offer/${offer.slug || offer.id}`}
                    className="text-[12px] font-sans font-semibold uppercase tracking-[0.14em] text-[#6E675F] hover:text-[#9A7B66] transition-colors"
                  >
                    View Details
                  </Link>
                </div>
              </div>
            </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
}
